import { BookmarksContext } from "context/bookmarks";
import React, { memo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { READER_THEMES } from 'utils/themes';
import { readerRefPropType } from 'utils/prop-types';
import Box from '@material-ui/core/Box';
import Icons from 'components/icons';
import ControlButton from 'components/common/control-button';
import FullScreenButton from 'components/full-screen-components/full-screen-button';
import MoreMenuButton from 'components/full-screen-components/more-menu-button';
import SettingsMenu from './settings-menu';
import NavigationMenu from './navigation-menu';
import useStyles from './control-panel.styles';
import ZoomIn from '@material-ui/icons/ZoomIn';
import ZoomOut from '@material-ui/icons/ZoomOut';
import SearchMenu from './search-menu';

const ControlPanel = memo((props) => {
  const { assetId, readerRef, activeTheme, isZoomActive, toggleZoom, isHeaderActive, annotationRefreshTrigger } = props;
  const classes = useStyles({ activeTheme });
  const isFixedLayout = !readerRef.current.navigator.publication.isReflowable;

  const { toggleBookmark, isBookmarkActive } = useContext(BookmarksContext);

  const onBookmarkClick = useCallback(() => {
    const location = readerRef.current.currentLocator;
    toggleBookmark(assetId, location);
  }, [assetId, readerRef.current.currentLocator, toggleBookmark]);

  const getBookmarkIcon = useCallback(() => {
      const location = readerRef.current.currentLocator;
      return isBookmarkActive(location) ?
          Icons.ReaderBookmarkRemove :
          Icons.ReaderBookmark;
  }, [isBookmarkActive, readerRef.current.currentLocator]);

  return (
    <Box className={classes.controlPanelContainer}>
      <SearchMenu
        readerRef={readerRef}
        controlBtnProps={{
          className: classes.controlButton,
          iconClassName: classes.controlIcon,
        }}
      />
      {isFixedLayout && (
      <ControlButton
        onClick={toggleZoom}
        className={classes.controlButton}
        iconClassName={classes.controlIcon}
        icon={isZoomActive ? ZoomOut : ZoomIn}
      />)}
      <FullScreenButton className={classes.controlButton} iconClassName={classes.controlIcon} />
      <ControlButton
        onClick={onBookmarkClick}
        className={classes.controlButton}
        iconClassName={classes.controlIcon}
        icon={getBookmarkIcon()}
      />
      <SettingsMenu
        readerRef={readerRef}
        isHeaderActive={isHeaderActive}
        controlBtnProps={{
          className: classes.controlButton,
          iconClassName: classes.controlIcon,
        }}
      />
      <NavigationMenu
        readerRef={readerRef}
        controlBtnProps={{
          className: classes.controlButton,
          iconClassName: classes.controlIcon,
        }}
        annotationRefreshTrigger={annotationRefreshTrigger}
      />
      {isHeaderActive &&
        <MoreMenuButton
          className={classes.moreMenu}
          readerRef={readerRef}
          itemClassName={classes.moreMenuItem}
          textClassName={classes.moreMenuText}
          controlBtnProps={{
            className: classes.controlButton,
            iconClassName: classes.controlIcon,
          }}
        />
      }
    </Box>
  );
});

ControlPanel.propTypes = {
  assetId: PropTypes.oneOf([PropTypes.string, PropTypes.number]).isRequired,
  readerRef: readerRefPropType,
  activeTheme: PropTypes.oneOf(Object.keys(READER_THEMES)),
  toggleZoom: PropTypes.func,
  isZoomActive: PropTypes.bool,
  isHeaderActive: PropTypes.bool,
  annotationRefreshTrigger: PropTypes.number,
};

export default ControlPanel;
